import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../lib/supabase.js";
import { useAuth } from "../lib/auth.js";
import { Button, Field, Input, Select } from "../lib/ui.js";
import { SectionCard, errorStyle, linkButtonStyle, mutedStyle, tdStyle, thStyle } from "./settings/common.js";
import { formatClock, formatDate } from "../lib/datetime.js";
import { TimeInput12 } from "../lib/TimeInput12.js";
import { addDaysYmd, endOfDayExclusive, startOfDayInZone, todayInZone, ymdOfInstant } from "../lib/zone.js";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
/** Monday first, the way a week reads on a shop door. */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

interface AvailabilityRow {
  id: string;
  weekday: number;
  start_time: string;
  end_time: string;
}

interface TimeOffRow {
  id: string;
  starts_at: string;
  ends_at: string;
  note: string | null;
}

/**
 * "My hours" for a staff login (migration 0060): the one staff member this login is linked to. Working hours decide when a
 * booking can be made (on the phone and on the web); time off blocks those days. The database only lets this login read and
 * change its own staff member's rows, so there is no staff picker here.
 */
export function MyHours() {
  const { activeBusinessId } = useAuth();
  const [me, setMe] = useState<{ id: string; name: string } | null | undefined>(undefined);
  const [availability, setAvailability] = useState<AvailabilityRow[]>([]);
  const [timeOff, setTimeOff] = useState<TimeOffRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [weekday, setWeekday] = useState("1");
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("17:00");
  const [offFrom, setOffFrom] = useState(() => todayInZone());
  const [offTo, setOffTo] = useState(() => todayInZone());
  const [offNote, setOffNote] = useState("");

  async function load() {
    if (!activeBusinessId) return;
    const { data: res } = await supabase.from("resources").select("id, name").eq("business_id", activeBusinessId).limit(1);
    const mine = ((res ?? []) as Array<{ id: string; name: string }>)[0] ?? null;
    setMe(mine);
    if (!mine) return;
    const [{ data: avail }, { data: off }] = await Promise.all([
      supabase.from("availability").select("id, weekday, start_time, end_time").eq("resource_id", mine.id).order("weekday").order("start_time"),
      supabase
        .from("availability_exceptions")
        .select("id, starts_at, ends_at, note")
        .eq("resource_id", mine.id)
        .eq("kind", "leave")
        .gt("ends_at", new Date().toISOString())
        .order("starts_at"),
    ]);
    setAvailability((avail ?? []) as AvailabilityRow[]);
    setTimeOff((off ?? []) as TimeOffRow[]);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessId]);

  async function addHours(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId || !me) return;
    setError(null);
    if (end <= start) {
      setError("The finish time must be after the start time.");
      return;
    }
    const { error: err } = await supabase.from("availability").insert({ business_id: activeBusinessId, resource_id: me.id, weekday: Number(weekday), start_time: start, end_time: end });
    if (err) {
      setError(err.message);
      return;
    }
    await load();
  }

  async function removeHours(id: string) {
    await supabase.from("availability").delete().eq("id", id);
    await load();
  }

  async function addTimeOff(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId || !me) return;
    setError(null);
    if (offTo < offFrom) {
      setError("The last day must be on or after the first day.");
      return;
    }
    const { error: err } = await supabase.from("availability_exceptions").insert({
      business_id: activeBusinessId,
      resource_id: me.id,
      kind: "leave",
      starts_at: startOfDayInZone(offFrom).toISOString(),
      ends_at: endOfDayExclusive(offTo).toISOString(),
      note: offNote.trim() || null,
    });
    if (err) {
      setError(err.message);
      return;
    }
    setOffNote("");
    await load();
  }

  async function removeTimeOff(id: string) {
    await supabase.from("availability_exceptions").delete().eq("id", id);
    await load();
  }

  if (me === undefined) return <p className="rc-loading">Loading...</p>;
  if (me === null) {
    return (
      <div>
        <h1 className="rc-h1" style={{ marginBottom: 16 }}>My hours</h1>
        <p style={mutedStyle}>Your login is not linked to a staff member yet. Ask your manager to link it on the Services &amp; staff page.</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <h1 className="rc-h1" style={{ marginBottom: -4 }}>My hours</h1>
      {error && <p style={errorStyle}>{error}</p>}

      <SectionCard title="Working hours" description={`When ${me.name} can take bookings. Add one row for each stretch of the day you work; a split shift is two rows.`}>
        <form onSubmit={addHours} style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
          <Field label="Day">
            <Select value={weekday} onChange={(e) => setWeekday(e.target.value)} style={{ width: 140 }}>
              {WEEK_ORDER.map((i) => (
                <option key={i} value={i}>
                  {WEEKDAYS[i]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Start">
            <TimeInput12 label="Start" value={start} onChange={setStart} />
          </Field>
          <Field label="Finish">
            <TimeInput12 label="Finish" value={end} onChange={setEnd} />
          </Field>
          <Button type="submit" variant="primary">
            Add
          </Button>
        </form>
        <div className="rc-table-scroll">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Day</th>
                <th style={thStyle}>Hours</th>
                <th style={thStyle} />
              </tr>
            </thead>
            <tbody>
              {[...availability]
                .sort((a, b) => WEEK_ORDER.indexOf(a.weekday) - WEEK_ORDER.indexOf(b.weekday) || a.start_time.localeCompare(b.start_time))
                .map((a) => (
                  <tr key={a.id}>
                    <td style={tdStyle}>{WEEKDAYS[a.weekday]}</td>
                    <td style={tdStyle}>
                      {formatClock(a.start_time)} - {formatClock(a.end_time)}
                    </td>
                    <td style={tdStyle}>
                      <button onClick={() => void removeHours(a.id)} style={{ ...linkButtonStyle, color: "var(--rc-danger)" }}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              {availability.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ ...tdStyle, ...mutedStyle }}>
                    No hours set yet, so no one can book you.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard title="Time off" description="Days you are away. Nothing can be booked with you on these days; bookings you already have stay, so move or cancel them first.">
        <form onSubmit={addTimeOff} style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
          <Field label="First day">
            <Input type="date" required value={offFrom} min={todayInZone()} onChange={(e) => setOffFrom(e.target.value)} />
          </Field>
          <Field label="Last day">
            <Input type="date" required value={offTo} min={offFrom} onChange={(e) => setOffTo(e.target.value)} />
          </Field>
          <Field label="Note (optional)">
            <Input value={offNote} onChange={(e) => setOffNote(e.target.value)} style={{ width: 200 }} />
          </Field>
          <Button type="submit" variant="primary">
            Add time off
          </Button>
        </form>
        <div className="rc-table-scroll">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {timeOff.map((t) => {
                const first = ymdOfInstant(t.starts_at);
                const last = addDaysYmd(ymdOfInstant(new Date(new Date(t.ends_at).getTime() - 1)), 0);
                return (
                  <tr key={t.id}>
                    <td style={tdStyle}>{first === last ? formatDate(t.starts_at) : `${formatDate(t.starts_at)} to ${formatDate(new Date(new Date(t.ends_at).getTime() - 1))}`}</td>
                    <td style={tdStyle}>{t.note ?? ""}</td>
                    <td style={tdStyle}>
                      <button onClick={() => void removeTimeOff(t.id)} style={{ ...linkButtonStyle, color: "var(--rc-danger)" }}>
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
              {timeOff.length === 0 && (
                <tr>
                  <td style={{ ...tdStyle, ...mutedStyle }}>No time off coming up.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
